import {Injector, NgModule} from '@angular/core';
import {AppComponent} from './app.component';
import {
  CORE_PORTAL_CROSS_CREATION_CONFIG,
  CORE_PORTAL_DASHBOARD_CONFIG,
  CORE_PORTAL_DATATABLE_PREDEFINED_VIEWS_CONFIG,
  CORE_PORTAL_DATATABLE_STANDARD_CONFIG,
  CORE_PORTAL_EXCLUDED_ROLE_TERMS,
  CORE_PORTAL_FORMLY_TEXT_TEMPLATE,
  CORE_PORTAL_ROLES_CONFIG,
  CORE_PORTAL_SIDEBAR_ITEMS,
  CorePortalModule
} from '@app/core-portal';
import {environment} from '../environments/environment';
import {StoreDevtoolsModule} from '@ngrx/store-devtools';
import {Product, RoleGeneralTerms, ServiceLocator} from '@app/core-shared';
import {CORE_PORTAL_FEATURE_STEREOTYPE_ADDITIONAL_FIELDS} from '@app/core-portal/features/settings/features/stereotypes';
import {AppRoutingModule} from './app-routing.module';
import {
  provideCrossCreationConfig,
  provideDashboardConfig,
  provideDatatablePredefinedViewConfig,
  provideDatatableStandardConfig,
  provideFormlyTextTemplateConfig,
  provideLinksConfig,
  provideRolesConfig,
  provideSettingsMenuConfig,
  provideSidebarItems,
  provideStereotypeAdditionalFields,
  provideTextTemplateContextConfig
} from './config';
import {TECH_PORTAL_LINKS_CONFIG_TOKEN, TechPortalModule} from '@app/tech-portal-lib';
import {TECH_PORTAL_TEXT_TEMPLATE_CONTEXT_CONFIG} from '@app/tech-portal/features/templates';
import {TechPortalFeatureTicketSettingsPrioritySetsModule} from '@app/tech-portal/features/ticket-settings/features/priority-sets';
import {TechPortalFeatureTicketSettingsStateMachinesModule} from '@app/tech-portal/features/ticket-settings/features/state-machines';
import {CorePortalInventoryNumbersModule} from '@app/core-portal/features/settings/features/inventory-numbers';
import {TranslateService} from '@ngx-translate/core';
import {TechPortalFeatureMissionsModule} from '@app/tech-portal/features/missions';
import {TechPortalFeatureTicketsModule} from '@app/tech-portal/features/tickets';
import {TechPortalFeatureResourcesModule} from '@app/tech-portal/features/resources';
import {TechPortalFeatureContractsModule} from '@app/tech-portal/features/contracts';
import {CorePortalFeatureMasterDataLocationsModule} from '@app/core-portal/features/master-data/features/locations';
import {CorePortalFeatureMasterDataCompaniesModule} from '@app/core-portal/features/master-data/features/companies';
import {NotificationRulesModule} from "@app/core-portal/features/settings/features/notification-rules";
import {
  CORE_PORTAL_SETTINGS_MENU_CONFIG,
  CorePortalSettingsMenuModule
} from "@app/core-portal/features/settings/menu";
import {TechPortalFeatureIssuesModule} from "@app/tech-portal/features/issues";
import {TechPortalFeatureControlsModule} from "@app/tech-portal/features/controls";
import {providePrimeNG} from 'primeng/config';
import {PrimengCustomLaraPreset} from './config';

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    AppRoutingModule,
    CorePortalModule.forRoot(environment, Product.TECH, 'one-portal', [
      'core-portal',
      'orga-portal',
      'tech-portal',
      'data-portal',
      ...(environment.additionalTranslations ?? [])
    ]),
    TechPortalModule.forRoot(),

    TechPortalFeatureTicketSettingsPrioritySetsModule.forFeature(),
    TechPortalFeatureTicketSettingsStateMachinesModule.forFeature(),
    CorePortalInventoryNumbersModule.forFeature(),
    NotificationRulesModule.forFeature(),
    TechPortalFeatureMissionsModule.forFeature(),
    TechPortalFeatureTicketsModule.forFeature(),
    TechPortalFeatureResourcesModule.forFeature(),
    TechPortalFeatureIssuesModule.forFeature(),
    TechPortalFeatureControlsModule.forFeature(),
    TechPortalFeatureContractsModule.forFeature(),
    CorePortalFeatureMasterDataLocationsModule.forFeature(),
    CorePortalFeatureMasterDataCompaniesModule.forFeature(),
    CorePortalSettingsMenuModule,

    ...(!environment.production ? [StoreDevtoolsModule.instrument({
      name: 'One Portal NGRX',
      maxAge: 50
      , connectInZone: true
    })] : [])
  ],
  providers: [
    {
      provide: CORE_PORTAL_EXCLUDED_ROLE_TERMS,
      useValue: [
        RoleGeneralTerms.OrgaUser,
        RoleGeneralTerms.DeviceUser,
        RoleGeneralTerms.TeamUser
      ]
    },

    { provide: CORE_PORTAL_DASHBOARD_CONFIG, useValue: provideDashboardConfig() },
    { provide: CORE_PORTAL_DATATABLE_STANDARD_CONFIG, useValue: provideDatatableStandardConfig() },
    { provide: CORE_PORTAL_DATATABLE_PREDEFINED_VIEWS_CONFIG, useValue: provideDatatablePredefinedViewConfig() },
    { provide: CORE_PORTAL_ROLES_CONFIG, useValue: provideRolesConfig() },
    { provide: CORE_PORTAL_SIDEBAR_ITEMS, useValue: provideSidebarItems() },

    { provide: CORE_PORTAL_FEATURE_STEREOTYPE_ADDITIONAL_FIELDS, useValue: provideStereotypeAdditionalFields() },
    { provide: TECH_PORTAL_LINKS_CONFIG_TOKEN, useValue: provideLinksConfig() },
    {
      provide: TECH_PORTAL_TEXT_TEMPLATE_CONTEXT_CONFIG,
      useFactory: provideTextTemplateContextConfig,
      deps: [TranslateService]
    },
    providePrimeNG({
      theme: {
        preset: PrimengCustomLaraPreset,
        options: {
          darkModeSelector: '.theme-dark'
        }
      }
    }),
    { provide: CORE_PORTAL_SETTINGS_MENU_CONFIG, useValue: provideSettingsMenuConfig() },
    { provide: CORE_PORTAL_CROSS_CREATION_CONFIG, useValue: provideCrossCreationConfig() },
    { provide: CORE_PORTAL_FORMLY_TEXT_TEMPLATE, useValue: provideFormlyTextTemplateConfig() }
  ],
  bootstrap: [
    AppComponent
  ]
})

export class AppModule {
  constructor(private injector: Injector) {
    ServiceLocator.injector = this.injector;
  }
}
